import * as React from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { PortalSignInError, completePortalSignIn, startPortalSignIn } from "./portal";
import { useSession } from "./session";

/**
 * `/auth/portal/callback`: where the portal drops the browser back with a
 * code. Everything happens here and then the route is gone from history,
 * so the code never sits in a URL the operator can share.
 */
export function PortalCallbackPage() {
  const adopt = useSession((s) => s.adopt);
  const navigate = useNavigate();
  const [error, setError] = React.useState<string>();
  const ran = React.useRef(false);

  React.useEffect(() => {
    // Strict mode mounts effects twice; the code is single-use.
    if (ran.current) return;
    ran.current = true;
    void (async () => {
      try {
        const { session, from } = await completePortalSignIn(window.location.search);
        adopt(session);
        navigate(from || "/", { replace: true });
      } catch (err) {
        setError(err instanceof PortalSignInError ? err.message : "The sign-in could not be completed.");
      }
    })();
  }, [adopt, navigate]);

  return (
    <div className="flex h-full items-center justify-center px-4">
      {error ? (
        <div className="w-full max-w-sm space-y-3">
          <h1 className="text-lg font-semibold">Could not sign in</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void startPortalSignIn()}>
              Try again
            </Button>
            <Button size="sm" variant="outline" onClick={() => void navigate("/login", { replace: true })}>
              Sign in another way
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Signing in with Vonix…</p>
      )}
    </div>
  );
}
